import { FormEvent, useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, AppSettings, loadSession } from "../lib/api.js";
import PeriodPicker from "../components/PeriodPicker.js";
import MetadataPicker from "../components/MetadataPicker.js";

const STARTUP_MODULES = [
  "overview", "saas-overview", "onboarding", "workspace", "billing",
  "security", "datasets", "warehouse-explorer", "public-portal",
  "programs", "dimensions", "tallies", "indicators", "mapping",
  "aggregates", "analytics", "reports", "monitoring", "replay",
  "settings", "aggregate-domains"
];

type Flash = { kind: "ok" | "error" | "info"; text: string } | null;

export default function Settings() {
  const session = loadSession();
  const isAdmin = session?.role === "admin";

  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [draft, setDraft] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash>(null);

  useEffect(() => {
    let cancelled = false;
    api.settings()
      .then((s) => { if (!cancelled) { setSettings(s); setDraft(s); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  const setField = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const onTest = async () => {
    setFlash(null);
    try {
      const result = await api.testConnection();
      setFlash({ kind: "ok", text: `Connection check ran. ${summarizeHealth(result)}` });
    } catch (e) {
      setFlash({ kind: "error", text: e instanceof Error ? e.message : String(e) });
    }
  };

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setSaving(true);
    setFlash(null);
    try {
      const next = await api.updateSettings(draft);
      setSettings(next);
      setDraft(next);
      setFlash({ kind: "ok", text: "Settings saved." });
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    setDraft(settings);
    setFlash({ kind: "info", text: "Pending changes reverted." });
  };

  if (loading || !draft) {
    return (
      <div className="page">
        <PageBreadcrumbs />
      <header className="page-head"><div><h1>Settings</h1></div></header>
        {error ? <div className="alert alert-error">{error}</div> : <SkeletonTable rows={5} columns={4} />}
      </div>
    );
  }

  const dirty = JSON.stringify(settings) !== JSON.stringify(draft);
  const profiles = draft.serviceProfiles ?? [];

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Settings</h1>
          <p>Two distinct surfaces below — global platform configuration (applies to every workspace and every user) and your current browser session (visible only to you).</p>
        </div>
        <div className="actions">
          <button className="btn" type="button" onClick={() => void onTest()} disabled={!isAdmin}>Test connections</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {flash && <div className={`alert alert-${flash.kind === "ok" ? "ok" : flash.kind === "error" ? "error" : "info"}`}>{flash.text}</div>}

      {!isAdmin && (
        <div className="alert alert-info">
          Settings are read-only for non-administrators. Sign in as an admin to make changes.
        </div>
      )}

      {profiles.length === 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          <strong>No DHIS2 instance configured.</strong>{" "}
          Add one under <a href="/app/instances">DHIS2 instances</a> before configuring reports and warehouse defaults.
        </div>
      )}

      <section className="section" style={{ marginBottom: 24 }}>
        <div className="section-head">
          <h2>Platform configuration</h2>
          <p className="soft" style={{ margin: "4px 0 0" }}>
            Applies to every workspace and every user on this deployment. Changes require an admin and persist to the platform database.
          </p>
        </div>
      </section>
      <form onSubmit={onSave}>
        <fieldset disabled={!isAdmin}>
          <legend>Environment</legend>
          <div className="form-grid">
            <div className="field">
              <label>Environment name</label>
              <input value={draft.environmentName} onChange={(e) => setField("environmentName", e.target.value)} required />
            </div>
            <div className="field">
              <label>Backend base URL</label>
              <input
                value={draft.backendBaseUrl}
                onChange={(e) => setField("backendBaseUrl", e.target.value)}
                placeholder="https://api.your-domain.com"
              />
              <small>Optional. Leave blank to use the current origin.</small>
            </div>
            <div className="field">
              <label>DHIS2 base URL (fallback)</label>
              <input
                value={draft.dhis2BaseUrl}
                onChange={(e) => setField("dhis2BaseUrl", e.target.value)}
                placeholder="https://dhis2.your-domain.org"
              />
              <small>Used only when no service profile is active. Manage instances on the DHIS2 instances page.</small>
            </div>
            <div className="field">
              <label>Active DHIS2 instance</label>
              {profiles.length === 0 ? (
                <>
                  <input disabled value="(none configured)" />
                  <small>
                    <a href="/app/instances">Add a DHIS2 instance</a> to enable this selector.
                  </small>
                </>
              ) : (
                <select
                  value={draft.activeServiceProfile}
                  onChange={(e) => setField("activeServiceProfile", e.target.value)}
                  required
                >
                  <option value="">— Select instance —</option>
                  {profiles.map((p) => (
                    <option key={p.environment} value={p.environment}>
                      {p.environment} — {p.baseUrl}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="field">
              <label>Default organisation unit</label>
              <MetadataPicker
                kind="orgUnits"
                value={draft.defaultOrgUnitId}
                onChange={(id) => setField("defaultOrgUnitId", id)}
                emptyLabel="— (auto from DHIS2 root) —"
              />
            </div>
            <div className="field">
              <label>Startup module</label>
              <select value={draft.startupModule} onChange={(e) => setField("startupModule", e.target.value)}>
                {STARTUP_MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset disabled={!isAdmin}>
          <legend>Warehouse &amp; rollups</legend>
          <div className="form-grid">
            <div className="field">
              <label>Warehouse provider</label>
              <input value={draft.warehouseProvider} disabled />
            </div>
            <div className="field">
              <label>Warehouse query limit</label>
              <input
                type="number" min={1} step={1}
                value={draft.warehouseQueryLimit}
                onChange={(e) => setField("warehouseQueryLimit", Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <div className="field">
              <label>Rollup refresh mode</label>
              <select
                value={draft.rollupRefreshMode ?? ""}
                onChange={(e) => setField("rollupRefreshMode", (e.target.value || undefined) as AppSettings["rollupRefreshMode"])}
              >
                <option value="">—</option>
                <option value="incremental">Incremental</option>
                <option value="scheduled">Scheduled</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div className="field">
              <label>Rollup strictness</label>
              <select
                value={draft.rollupStrictness ?? ""}
                onChange={(e) => setField("rollupStrictness", (e.target.value || undefined) as AppSettings["rollupStrictness"])}
              >
                <option value="">—</option>
                <option value="strict">Strict</option>
                <option value="permissive">Permissive</option>
              </select>
            </div>
            <div className="field">
              <label>Aggregate validation</label>
              <select
                value={draft.aggregateValidationStrictness ?? ""}
                onChange={(e) => setField("aggregateValidationStrictness", (e.target.value || undefined) as AppSettings["aggregateValidationStrictness"])}
              >
                <option value="">—</option>
                <option value="strict">Strict</option>
                <option value="warning">Warning</option>
              </select>
            </div>
            <div className="field">
              <label>Period rollup mode</label>
              <select
                value={draft.periodRollupMode ?? ""}
                onChange={(e) => setField("periodRollupMode", (e.target.value || undefined) as AppSettings["periodRollupMode"])}
              >
                <option value="">—</option>
                <option value="monthly-to-quarterly-yearly">Monthly → quarterly → yearly</option>
                <option value="flat">Flat</option>
              </select>
            </div>
            <div className="field">
              <label>Org unit rollup mode</label>
              <select
                value={draft.orgUnitRollupMode ?? ""}
                onChange={(e) => setField("orgUnitRollupMode", (e.target.value || undefined) as AppSettings["orgUnitRollupMode"])}
              >
                <option value="">—</option>
                <option value="hierarchy">Hierarchy</option>
                <option value="flat">Flat</option>
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset disabled={!isAdmin}>
          <legend>Access control</legend>
          <div className="form-grid">
            <div className="field">
              <label>Role mode</label>
              <select value={draft.roleMode} onChange={(e) => setField("roleMode", e.target.value as AppSettings["roleMode"])}>
                <option value="strict">Strict (deny on ambiguity)</option>
                <option value="permissive">Permissive</option>
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset disabled={!isAdmin}>
          <legend>Report defaults</legend>
          <div className="form-grid">
            <div className="field">
              <label>Default program</label>
              <MetadataPicker
                kind="programs"
                value={draft.reportDefaults.defaultProgramId}
                onChange={(id) => setField("reportDefaults", { ...draft.reportDefaults, defaultProgramId: id })}
              />
            </div>
            <div className="field">
              <label>Default indicator</label>
              <MetadataPicker
                kind="indicators"
                value={draft.reportDefaults.defaultIndicatorId}
                onChange={(id) => setField("reportDefaults", { ...draft.reportDefaults, defaultIndicatorId: id })}
              />
            </div>
            <div className="field">
              <label>Default period</label>
              <PeriodPicker
                value={draft.reportDefaults.defaultPeriodId}
                onChange={(v) => setField("reportDefaults", { ...draft.reportDefaults, defaultPeriodId: v })}
                required
              />
              <small>Prefer a relative period (e.g. <code>LAST_MONTH</code>) so reports roll forward automatically.</small>
            </div>
          </div>
        </fieldset>

        <div className="row-wrap" style={{ justifyContent: "flex-end", marginTop: 8 }}>
          <button type="button" className="btn" onClick={onReset} disabled={!dirty || saving}>Reset</button>
          <button type="submit" className="btn btn-primary" disabled={!isAdmin || !dirty || saving}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>

      <section className="section" style={{ marginTop: 32 }}>
        <div className="section-head">
          <h2>Your session</h2>
          <p className="soft" style={{ margin: "4px 0 0" }}>
            This browser only — read-only diagnostic info about your current sign-in. Sign out to clear it.
          </p>
        </div>
        <dl className="dl-kv">
          <dt>User</dt><dd><code>{session?.userId ?? "—"}</code></dd>
          <dt>Role</dt><dd>{session?.role ?? "—"}</dd>
          <dt>Auth type</dt><dd>{session?.authType ?? "—"}</dd>
          <dt>Workspace</dt><dd>{session?.workspaceId ?? <span className="soft">default</span>}</dd>
          <dt>Token expires</dt><dd>{session ? new Date(session.expiresAt).toLocaleString() : "—"}</dd>
        </dl>
      </section>
    </div>
  );
}

function summarizeHealth(result: Record<string, unknown>): string {
  if (!result || typeof result !== "object") return "";
  if ("activeEnvironment" in result) {
    return `Active environment: ${String((result as { activeEnvironment: unknown }).activeEnvironment ?? "—")}`;
  }
  return "";
}
